import { ComponentType } from 'react';
import styled from 'styled-components';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { colors, s16, s4, s8, timeFastMs } from '@ardoq/design-tokens';
import { text1 } from '@ardoq/typography';
import { FlexBox, FlexBoxProps } from '@ardoq/layout';
import { SortOrder } from '@ardoq/api-types';

const SortIconStyle = styled(Icon)``;

export const SortIcon = ({
  order,
  iconSize,
  ...props
}: {
  order: SortOrder;
  iconSize?: IconSize;
}) => (
  <SortIconStyle
    {...props}
    iconSize={iconSize}
    iconName={
      order === SortOrder.ASC ? IconName.ARROW_UPWARD : IconName.ARROW_DOWNWARD
    }
  />
);
SortIcon.displayName = 'SortIcon';

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  color: ${colors.textDefault};
`;
Table.displayName = 'Table';

const Tfoot = styled.tfoot``;

const Thead = styled.thead`
  padding: 2px;
  background: ${colors.surfaceDefault};
  position: relative;

  &::after,
  &::before {
    pointer-events: none;
    display: block;
    position: absolute;
    top: -${s4};
    left: 0;
    height: 100%;
    width: 100%;
    content: ' ';
  }

  &::after {
    border-bottom: 1px solid ${colors.borderAction};
  }

  &::before {
    opacity: 0;
    transition: opacity ${timeFastMs} ease-in-out;
    box-shadow: rgb(0 0 0 / 10%) 0px 2px 5px;
  }

  &.show-shadow {
    &::before {
      opacity: 1;
    }
  }
`;
Thead.displayName = 'Thead';

const Tr = styled('tr')<{
  /* number added here, just to allow simplifications like index%2 */
  $even?: boolean | number;
}>``;
Tr.displayName = 'Tr';

const Th = styled.th`
  ${text1};
  /* TODO broken rule of text token */
  font-weight: 600;
  color: ${colors.textDefault};
  padding: 0;
  text-align: left;
  height: 44px; /* 40px + 4px extra above the separator line */
  padding-bottom: ${s8};
`;
Th.displayName = 'Th';

export const ThButtonWrapper = styled.div<{
  $showContentOnHoverOnly?: boolean;
}>`
  opacity: ${({ $showContentOnHoverOnly }) =>
    $showContentOnHoverOnly ? 0 : 1};
`;
ThButtonWrapper.displayName = 'ThButtonWrapper';

const ThBody = styled.div`
  display: flex;
  padding: 0 ${s16} 0 ${s16};
  align-items: center;
  justify-content: space-between;

  &:hover {
    ${ThButtonWrapper} {
      opacity: 1;
    }
  }
`;
ThBody.displayName = 'ThBody';

const Tbody = styled.tbody`
  ${Tr.toString()} {
    background: ${colors.surfaceDefault};

    &:hover {
      background: ${colors.surfaceHover};
    }
  }
`;
Tbody.displayName = 'Tbody';

const Td = styled.td`
  ${text1};
  height: 40px;
  padding: 0 ${s16} 0 ${s16};
`;
Td.displayName = 'Td';

const ScrollableBody = styled.div`
  overflow-y: auto;
`;
ScrollableBody.displayName = 'ScrollableBody';

const DragHandler = styled(Icon).attrs({
  iconSize: IconSize.MEDIUM,
  iconName: IconName.DRAG_INDICATOR,
})`
  display: flex;
  align-self: center;
  cursor: grab;
  color: ${colors.icon};
`;
DragHandler.displayName = 'DragHandler';

export const ExpandedRowSpace = styled(FlexBox).attrs(
  ({ gap = 'medium' }: FlexBoxProps) => ({ gap })
)`
  background: ${colors.surfaceDefault};
  min-height: 40px;
  border-top: 1px solid ${colors.borderAction};
  border-bottom: 1px solid ${colors.borderAction};
  padding: ${s16};
`;

export const newTableTheme: Record<string, ComponentType<any>> = {
  ExpandedRowSpace,
  Table,
  Td,
  Tbody,
  ScrollableBody,
  Thead,
  Tfoot,
  BodyTr: Tr,
  HeadTr: Tr,
  SortIcon,
  Th,
  ThBody,
};
